import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Held in front of the dashboard for a student whose account is not
 * activated. Mirrors the mobile PendingActivationScreen: rules deny show
 * content until `accountStatus == 'activated'`, so letting them through only
 * shows a dashboard of load errors.
 *
 * Reachable because a password set through Firebase's own reset flow signs
 * someone in without activating them. A disabled account is a teacher's
 * decision rather than an unfinished signup, so it is not offered a resend.
 */
const PendingAccountScreen: React.FC<{
  email?: string;
  disabled: boolean;
  onSignOut: () => void;
}> = ({ email, disabled, onSignOut }) => {
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const handleResend = async () => {
    if (!email || resendState !== 'idle') return;
    setResendState('sending');
    try {
      await httpsCallable(functions, 'publicResendActivationEmail')({ email });
    } catch (err) {
      // Same stance as the activation page: never confirm whether an account
      // exists, so a failure reads the same as a send.
      console.error('Failed to resend activation email:', err);
    }
    setResendState('sent');
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold text-fg mb-4">
          {disabled ? 'Your account has been disabled' : "Let's finish setting up your account"}
        </h1>
        <p className="text-fg-secondary mb-6">
          {disabled
            ? 'A teacher on your show has disabled this account. Speak to them if you think this is a mistake.'
            : `Check ${email || 'your email'} for an activation link to finish setting up your account. The link confirms your email and unlocks your show.`}
        </p>
        {!disabled && (
          resendState === 'sent' ? (
            <p className="text-fg-secondary mb-6">
              If that email is registered, a new activation link is on its way.
            </p>
          ) : (
            <button
              onClick={() => { void handleResend(); }}
              disabled={!email || resendState === 'sending'}
              className="btn-primary mb-4"
            >
              {resendState === 'sending' ? 'Sending...' : 'Email me a new activation link'}
            </button>
          )
        )}
        <button
          onClick={onSignOut}
          className="block mx-auto mt-2 text-sm text-fg-secondary hover:text-fg underline"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default PendingAccountScreen;
